import React, { useEffect, useState } from 'react';
import Navbar from './homepage/components/Navbar';
import Footer from './homepage/components/Footer';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

type TagType = 'Neu' | 'Behoben' | 'Verbessert';

interface ChangelogItem {
  text: string;
  tag: TagType;
}

interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  items: ChangelogItem[];
}

const TAG_COLORS: Record<TagType, { color: string; bg: string }> = {
  Neu:        { color: '#10b981', bg: '#ecfdf5' },
  Behoben:    { color: '#ef4444', bg: '#fef2f2' },
  Verbessert: { color: '#2563eb', bg: '#eff6ff' },
};

const FALLBACK_ENTRIES: ChangelogEntry[] = [
  {
    version: 'v2.8',
    date: '10. März 2026',
    title: 'UI-Touren, Twilio & Help Center',
    items: [
      { text: 'Geführte UI-Touren für Dashboard, Klienten, Nachrichten und Rechnungen', tag: 'Neu' },
      { text: 'Twilio SMS-Integration vorbereitet (Channel-Config + Webhooks)', tag: 'Neu' },
      { text: 'Help Center mit 22 Hilfe-Artikeln in 8 Kategorien', tag: 'Neu' },
      { text: 'Kontextbezogener Hilfe-Button und Slide-in Panel', tag: 'Neu' },
      { text: 'Umlaut-Darstellung in E-Mails und PDFs korrigiert', tag: 'Behoben' },
      { text: 'Onboarding-Banner für neue Nutzer', tag: 'Verbessert' },
    ],
  },
  {
    version: 'v2.7',
    date: '8. März 2026',
    title: 'Homepage-Redesign & Unified Messaging',
    items: [
      { text: 'Komplett überarbeitete öffentliche Homepage mit 9 Sections', tag: 'Neu' },
      { text: 'Unified Messaging: In-App Chat mit Echtzeit-Updates via WebSocket', tag: 'Neu' },
      { text: 'Auto-Reply-System (Im Coaching, Geschäftszeiten, Urlaub)', tag: 'Neu' },
      { text: 'E-Mail-Benachrichtigungen bei neuen Chat-Nachrichten', tag: 'Neu' },
      { text: 'Chat-Archivierung (manuell + automatisch)', tag: 'Verbessert' },
      { text: 'Portal-Chat-Tab für Klienten', tag: 'Verbessert' },
    ],
  },
  {
    version: 'v2.6',
    date: '5. März 2026',
    title: 'Team-Management & White Label',
    items: [
      { text: 'Team/Praxis-Verwaltung mit Mitgliedern, Einladungen und Rollen', tag: 'Neu' },
      { text: 'White Label: eigenes Logo in PDFs und Coach-Branding im Portal', tag: 'Neu' },
      { text: 'Abo-basiertes Mitgliederlimit für Teams', tag: 'Neu' },
      { text: 'Klienten-Portal: Termine-Tab mit Split-View Kalender', tag: 'Verbessert' },
      { text: 'Portal-Registrierung: Live-Code-Validierung und Passwort-Stärke-Balken', tag: 'Verbessert' },
      { text: 'Portal-Dashboard mit kompaktem 2-Spalten-Grid aufgeräumt', tag: 'Verbessert' },
    ],
  },
  {
    version: 'v2.5',
    date: '2. März 2026',
    title: 'Wartungsmodul & Qualität',
    items: [
      { text: 'Wartungsmodul mit Health Monitoring und Anomalie-Erkennung', tag: 'Neu' },
      { text: 'Weekly Health-Report per E-Mail und Monthly PDF-Report', tag: 'Neu' },
      { text: 'Qualitätsmanagement ISO 9001 Modul', tag: 'Neu' },
      { text: 'Responsive Layout mit 3 Breakpoints und Mobile Hamburger-Menü', tag: 'Verbessert' },
      { text: 'Dashboard v2: KPI-Karten mit Trend-Badges und Aktivitäten-Timeline', tag: 'Verbessert' },
    ],
  },
  {
    version: 'v2.4',
    date: '28. Februar 2026',
    title: 'Methoden-Bibliothek & Support',
    items: [
      { text: '44 Coaching-Methoden in 15 Kategorien mit Shop-System', tag: 'Neu' },
      { text: 'Support-Tickets für Coaches und Klienten mit Änderungsanträgen', tag: 'Neu' },
      { text: 'Abo-System mit 4 Plänen und Onboarding-Wizard', tag: 'Neu' },
      { text: 'DSGVO-Modul: Daten-Export, Löschung und Audit-Trail', tag: 'Verbessert' },
      { text: 'Cookie-Banner optimiert', tag: 'Behoben' },
    ],
  },
];

function SkeletonLoader() {
  const bar = (w: string, h = 16): React.CSSProperties => ({
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: 6, width: w, height: h,
  });

  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{
          background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
          padding: '16px 20px', marginBottom: 10,
        }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={bar('50px', 22)} />
            <div style={bar('100px', 14)} />
            <div style={{ flex: 1 }} />
            <div style={bar('50%', 16)} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ChangelogCard({ entry, defaultOpen }: { entry: ChangelogEntry; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  // Count items by tag
  const counts = entry.items.reduce((acc, item) => {
    acc[item.tag] = (acc[item.tag] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div style={{
      background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      marginBottom: 8, overflow: 'hidden',
    }}>
      {/* Header — always visible, clickable */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', border: 'none', background: 'transparent',
          padding: '14px 20px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
          textAlign: 'left',
        }}
      >
        <span style={{
          fontFamily: "'Inter', -apple-system, sans-serif",
          fontSize: 12, fontWeight: 700,
          color: '#2563eb', background: '#eff6ff',
          padding: '3px 10px', borderRadius: 12,
          flexShrink: 0,
        }}>
          {entry.version}
        </span>
        <span style={{
          fontFamily: "'Inter', -apple-system, sans-serif",
          fontSize: 12, color: '#9ca3af', flexShrink: 0,
        }}>
          {entry.date}
        </span>
        <span style={{
          fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
          fontSize: 14, fontWeight: 600, color: '#0f172a',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {entry.title}
        </span>
        {/* Mini tag counts */}
        <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {counts['Neu'] && (
            <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
              +{counts['Neu']}
            </span>
          )}
          {counts['Behoben'] && (
            <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
              {counts['Behoben']} Fix
            </span>
          )}
          {counts['Verbessert'] && (
            <span style={{ fontSize: 11, color: '#2563eb', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
              {counts['Verbessert']} Upd
            </span>
          )}
        </span>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s', flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Items — collapsible */}
      {open && (
        <div style={{
          padding: '0 20px 16px',
          borderTop: '1px solid #f3f4f6',
        }}>
          <ul style={{
            margin: '12px 0 0', padding: 0, listStyle: 'none',
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            {entry.items.map((item, j) => {
              const tagCfg = TAG_COLORS[item.tag];
              return (
                <li key={j} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <span style={{
                    fontFamily: "'Inter', -apple-system, sans-serif",
                    fontSize: 10, fontWeight: 700,
                    color: tagCfg.color, background: tagCfg.bg,
                    padding: '2px 8px', borderRadius: 4,
                    whiteSpace: 'nowrap', marginTop: 2,
                    flexShrink: 0,
                  }}>
                    {item.tag}
                  </span>
                  <span style={{
                    fontFamily: "'Inter', -apple-system, sans-serif",
                    fontSize: 13, color: '#374151', lineHeight: 1.5,
                  }}>
                    {item.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ChangelogPage() {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/public/changelog`)
      .then((res) => {
        const data = res.data;
        if (Array.isArray(data) && data.length > 0) {
          const mapped: ChangelogEntry[] = data.map((e: any) => ({
            version: e.version,
            date: e.published_at
              ? new Date(e.published_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })
              : e.date || '',
            title: e.title,
            items: (e.items || []).map((item: any) => ({
              text: item.text,
              tag: item.tag === 'feature' ? 'Neu'
                : item.tag === 'fix' ? 'Behoben'
                : item.tag === 'improve' ? 'Verbessert'
                : item.tag as TagType,
            })),
          }));
          setEntries(mapped);
        } else {
          setEntries(FALLBACK_ENTRIES);
        }
      })
      .catch(() => {
        setEntries(FALLBACK_ENTRIES);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="homepage" style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <Navbar />

      {/* Hero — compact */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #1e3a5f 100%)',
        padding: '140px 24px 48px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, sans-serif",
          fontSize: 32, fontWeight: 800, color: '#fff', margin: 0,
        }}>
          Changelog
        </h1>
        <p style={{
          fontFamily: "'Inter', -apple-system, sans-serif",
          fontSize: 15, color: 'rgba(255,255,255,0.7)', marginTop: 8,
        }}>
          Neuigkeiten und Verbesserungen
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px 80px' }}>
        {loading && <SkeletonLoader />}

        {!loading && entries.length === 0 && (
          <div style={{
            background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb',
            padding: '40px 24px', textAlign: 'center',
          }}>
            <p style={{
              fontFamily: "'Inter', -apple-system, sans-serif",
              fontSize: 15, color: '#6b7280', margin: 0,
            }}>
              Noch keine Einträge vorhanden.
            </p>
          </div>
        )}

        {!loading && entries.length > 0 && entries.map((entry, idx) => (
          <ChangelogCard key={idx} entry={entry} defaultOpen={idx === 0} />
        ))}
      </div>

      <Footer />
    </div>
  );
}
